import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useMemo } from 'react';
import { useDeleteGooglePlayStoreChannel } from '../../../api/delete-google-play-store-channel.api';
import { queryClient } from '@/lib/react-query';
import { GET_LIST_GOOGLE_PLAY_STORE_CHANNEL_QUERY_KEY } from '../../../api/get-list-google-play-store-channel.api';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';

const TOAST_MESSAGES_DELETE_GOOGLE_PLAY_STORE_CHANNEL = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete google play store channel integration!',
};

export type TDeleteGooglePlayStoreChannelModalType = 'DeleteGooglePlayStoreChannelModal';
export interface IDeleteGooglePlayStoreChannelModalProps {
  organizationChannelId: string;
}

const DeleteGooglePlayStoreChannelModal = memo(({ organizationChannelId }: IDeleteGooglePlayStoreChannelModalProps) => {
  const showToast = useCustomToast();
  const channelModalRootActions = useChannelModalRootActionsStore();

  const isOpenDeleteGooglePlayStoreChannelModal = useMemo(() => {
    return channelModalRootActions.checkIsOpenModal('DeleteGooglePlayStoreChannelModal');
  }, [channelModalRootActions]);

  const mutationDeleteIntegrationGooglePlayStoreChannel = useDeleteGooglePlayStoreChannel({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_GOOGLE_PLAY_STORE_CHANNEL.error;
        showToast({ type: 'error', message: errorMessage });
      },
      onSuccess: () => {
        Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_GOOGLE_PLAY_STORE_CHANNEL_QUERY_KEY] })]);
        channelModalRootActions.closeModal('DeleteGooglePlayStoreChannelModal');
        showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_GOOGLE_PLAY_STORE_CHANNEL.success });
      },
    },
  });

  const handleDelete = useCallback(() => {
    mutationDeleteIntegrationGooglePlayStoreChannel.mutate({
      organizationChannelId: organizationChannelId || '',
    });
  }, [mutationDeleteIntegrationGooglePlayStoreChannel, organizationChannelId]);

  const handleClose = useCallback(() => {
    channelModalRootActions.closeModal('DeleteGooglePlayStoreChannelModal');
  }, [channelModalRootActions]);

  return (
    <ModalWithConfirmation
      isOpen={isOpenDeleteGooglePlayStoreChannelModal}
      onClose={handleClose}
      onDelete={handleDelete}
      isLoading={mutationDeleteIntegrationGooglePlayStoreChannel.isPending}
      title="Delete Google Play Store Integration"
      description="Do you want to delete this integration?"
      bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
      confirmText="delete my channel"
    />
  );
});

DeleteGooglePlayStoreChannelModal.displayName = 'DeleteGooglePlayStoreChannelModal';

export default DeleteGooglePlayStoreChannelModal;
